/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';

import { getSearchResults } from 'shared-ui/api/search/search.api';

import { buildSearchResultsParams } from '../actions/search';

/** How long a search response is reused before it's fetched again */
const SEARCH_STALE_TIME = 60 * 1000;

export const searchQueryKeys = {
    all: ['search'] as const,
    results: (params: string) => [...searchQueryKeys.all, 'results', params] as const,
};

/**
 * Number of results for a search state, from the request `fetchSearchResults`
 * sends (without looking up the user's position). Only asks for one result,
 * since `total` doesn't depend on the page size.
 */
export function useSearchResultsTotalQuery(
    search: object,
    { enabled }: { enabled: boolean }
) {
    // Building the request URL copies `search`, so skip it while disabled
    const params = React.useMemo(() => {
        if (!enabled) {
            return null;
        }
        const searchParams = buildSearchResultsParams(search);
        // Not 0: the search API treats it as its default page of 20
        searchParams.set('size', '1');
        return searchParams.toString();
    }, [search, enabled]);

    return useQuery({
        queryKey: searchQueryKeys.results(params ?? ''),
        queryFn:
            params === null
                ? skipToken
                : ({ signal }) => getSearchResults(new URLSearchParams(params), signal),
        select: (data) => data.total,
        staleTime: SEARCH_STALE_TIME,
    });
}
